import { AlertCloseType, IAlertCloseProps } from 'app/components/presentation/alert/AlertClose';
import { AlertTitleType, IAlertTitleProps } from 'app/components/presentation/alert/AlertTitle';
import { getTextStyles } from 'app/components/presentation/text/styles';
import SvgAlertError from 'app/components/svgs/AlertError';
import SvgAlertInfo from 'app/components/svgs/AlertInfo';
import SvgAlertSuccess from 'app/components/svgs/AlertSuccess';
import SvgAlertWarning from 'app/components/svgs/AlertWarning';
import { COLOR_PALETTE } from 'app/constants/ColorPalette';
import { BOX_SHADOW_A } from 'app/constants/Styles';
import { useCss } from 'app/utils/css';
import React, { ComponentType, FunctionComponent, ReactElement, SVGProps } from 'react';

export const enum AlertVariant {
  Danger = 'alert_danger',
  Info = 'alert_info',
  Success = 'alert_success',
  Warning = 'alert_warning',
}

// Require that the icon be a component type that can take SVG props.
type AlertIconComponentType = ComponentType<SVGProps<SVGSVGElement>>;

// Maps alert variants to their respective icons.
const DEFAULT_ICON_MAP: {
  [I in AlertVariant]: AlertIconComponentType;
} = {
  [AlertVariant.Danger]: SvgAlertError,
  [AlertVariant.Info]: SvgAlertInfo,
  [AlertVariant.Success]: SvgAlertSuccess,
  [AlertVariant.Warning]: SvgAlertWarning,
};

interface IProps {
  closeElement?: ReactElement<IAlertCloseProps, AlertCloseType>;
  /**
   * Should the alert role be applied to the AlertBox? When using the AlertBox, it should be determined
   * whether the box is actually informative or time sensitive, or is merely being used to contrast
   * information on a page. When implementing a feature, be sure to ask whoever is in charge of the
   * specification whether the AlertBox should provide the same kind of emphasis to assistive technologies.
   *
   * Please look at the stories to see some things to be careful of.
   *
   * ARIA alert specificaiton: https://www.w3.org/TR/wai-aria-1.1/#alert
   * ARIA status specification: https://www.w3.org/TR/wai-aria-1.1/#status
   *
   * Guidlines: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_alert_role#Description
   */
  role?: string;
  shadow?: boolean;
  titleElement?: ReactElement<IAlertTitleProps, AlertTitleType>;
  variant: AlertVariant;
}

/**
 * Presents an alert as shown in the styleguide: https://xd.adobe.com/view/87b523a9-7ae0-4f87-523b-fa245b75267c-f184/screen/94a81978-5809-4601-85b9-409905a19cac/.
 *
 * For examples of usage, please reference the stories.
 */
const AlertBox: FunctionComponent<IProps> = ({
  closeElement,
  children,
  role,
  shadow = true,
  titleElement,
  variant,
}) => {
  const css = useCss();
  const Icon: AlertIconComponentType = DEFAULT_ICON_MAP[variant];

  return (
    <section
      className={css('container', {
        alignItems: 'center',
        backgroundColor:
          variant === AlertVariant.Danger
            ? COLOR_PALETTE.red.lighter
            : variant === AlertVariant.Info
            ? COLOR_PALETTE.blue.lighter
            : variant === AlertVariant.Success
            ? COLOR_PALETTE.green.lighter
            : variant === AlertVariant.Warning
            ? COLOR_PALETTE.yellow.lighter
            : undefined,
        border: 'none',
        borderLeftColor:
          variant === AlertVariant.Danger
            ? COLOR_PALETTE.red.base
            : variant === AlertVariant.Info
            ? COLOR_PALETTE.blue.base
            : variant === AlertVariant.Success
            ? COLOR_PALETTE.green.base
            : variant === AlertVariant.Warning
            ? COLOR_PALETTE.yellow.base
            : undefined,
        borderLeftStyle: 'solid',
        borderLeftWidth: '5px',
        boxShadow: shadow ? BOX_SHADOW_A : 'none',
        display: 'flex',
        padding: '18px 12px 18px 18px',
        flexGrow: 1,
        textAlign: 'left',
      })}
      role={role}
    >
      <div aria-hidden className={css('icon', { flexGrow: 0, flexShrink: 0, lineHeight: 1, marginRight: '15px' })}>
        <Icon height="26px" width="26px" />
      </div>

      <div className={css('content', { display: 'flex', flexDirection: 'column', flexGrow: 1 })}>
        {titleElement && (
          <header className={css('title', { marginBottom: children ? '7px' : 0 })}>
            {titleElement}
          </header>
        )}

        <div
          className={css('children', {
            ...getTextStyles({
              color: COLOR_PALETTE.grey.darker,
              fontSize: '14px',
              letterSpacing: '0.8px',
            }),
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
          })}
        >
          {children}
        </div>
      </div>

      {closeElement && <div children={closeElement} className={css('close', { marginLeft: '15px' })} />}
    </section>
  );
};

export default AlertBox;
